import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';
import type { Client } from '@/types/api';

interface ClientFormState {
  id?: number;
  name: string;
  email: string;
  phone: string;
  isActive: boolean;
  notes: string;
}

const createEmptyClientForm = (): ClientFormState => ({
  id: undefined,
  name: '',
  email: '',
  phone: '',
  isActive: true,
  notes: '',
});

const ClientsManagement: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('ClientsManagement deve ser usado dentro de AppProvider');
  }

  const { adminClients, loadAdminClients, updateClient, deleteClient } = context;

  const [formState, setFormState] = useState<ClientFormState | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filteredClients = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return adminClients.filter(client =>
      client.name?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term) ||
      client.phone?.toLowerCase().includes(term),
    );
  }, [adminClients, searchTerm]);

  const startEditing = (clientId: number) => {
    const client = adminClients.find(item => item.id === clientId);
    if (!client) return;

    setFormState({
      id: client.id,
      name: client.name,
      email: client.email ?? '',
      phone: client.phone ?? '',
      isActive: client.is_active ?? true,
      notes: client.notes ?? '',
    });
    setFeedback(null);
    setError(null);
  };

  const cancelEditing = () => {
    setFormState(null);
    setFeedback(null);
    setError(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormState(prev => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFormState(prev => (prev ? { ...prev, isActive: checked } : prev));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState || !formState.id) return;

    const payload: Partial<Client> = {
      name: formState.name.trim(),
      email: formState.email.trim() || null,
      phone: formState.phone.trim() || null,
      notes: formState.notes.trim() || null,
      is_active: formState.isActive,
    };

    if (!payload.name) {
      setError('Informe o nome do cliente.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setFeedback(null);

    try {
      await updateClient(formState.id, payload);
      setFeedback('Cliente atualizado com sucesso.');
      setFormState(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar cliente.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (clientId: number) => {
    const confirmation = window.confirm('Tem certeza que deseja remover este cliente?');
    if (!confirmation) return;

    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await deleteClient(clientId);
      setFeedback('Cliente removido com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao remover cliente.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await loadAdminClients();
      setFeedback('Clientes recarregados.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao recarregar clientes.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gerenciamento de Clientes</h2>
        <div className="section-actions">
          <input
            type="search"
            className="section-search-input"
            placeholder="Buscar por nome, e-mail ou telefone"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            disabled={submitting}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={submitting}
          >
            Recarregar
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {feedback && <p className="success">{feedback}</p>}

      {formState && (
        <form onSubmit={handleSubmit} className="admin-form admin-client-form">
          <h3>Editar cliente</h3>
          <label>
            Nome
            <input
              name="name"
              value={formState.name}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </label>
          <label>
            E-mail
            <input
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              disabled={submitting}
            />
          </label>
          <label>
            Telefone
            <input
              name="phone"
              value={formState.phone}
              onChange={handleChange}
              disabled={submitting}
            />
          </label>
          <label>
            Observações
            <textarea
              name="notes"
              rows={3}
              value={formState.notes}
              onChange={handleChange}
              disabled={submitting}
            />
          </label>
          <label>
            Ativo
            <input
              type="checkbox"
              checked={formState.isActive}
              onChange={handleCheckboxChange}
              disabled={submitting}
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="save-button" disabled={submitting}>
              Salvar
            </button>
            <button type="button" className="cancel-button" onClick={cancelEditing} disabled={submitting}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {filteredClients.length === 0 ? (
        <p>Nenhum cliente cadastrado.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Telefone</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map(client => (
              <tr key={client.id}>
                <td>{client.id}</td>
                <td>{client.name}</td>
                <td>{client.email ?? '—'}</td>
                <td>{client.phone ?? '—'}</td>
                <td>{client.is_active ? 'Ativo' : 'Inativo'}</td>
                <td className="actions">
                  <div className="admin-actions">
                    <button
                      type="button"
                      className="icon-button icon-button--edit"
                      onClick={() => startEditing(client.id)}
                      disabled={submitting}
                      title="Editar cliente"
                    >
                      ✏️
                      <span className="sr-only">Editar</span>
                    </button>
                    <button
                      type="button"
                      className="icon-button icon-button--delete"
                      onClick={() => handleDelete(client.id)}
                      disabled={submitting}
                      title="Remover cliente"
                    >
                      🗑️
                      <span className="sr-only">Remover</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ClientsManagement;
